import React, {useState} from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import {useFormik} from 'formik';
import Grid from '@mui/material/Grid';
import {MenuItem, FormControl, InputLabel, Select} from '@mui/material';
import {
  DialogActionButton,
  TextField,
} from '@finpay-development/shared-components';
import {
  ClientStatusCardViewModel,
} from '../../../shared/model/client-status-card';
import DialogActions from '@mui/material/DialogActions';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../shared/state/root-reducer';
import {
  createEmployer,
} from '../../../meta-data/state/meta-data-thunk';
import * as Yup from 'yup';
import {showErrorStatus} from '../../../security/state/user-slice';
import {AppDispatch} from '../../../shared/state/store';

interface ClientEmployerModalProps{
  modalOpenState: {
    isModalOpen: boolean;
    setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  }
  selectedClientId: number
}

export const ClientEmployerModal = ({modalOpenState, selectedClientId}: ClientEmployerModalProps) => {

  const clients = useSelector(
      (state: RootState) =>
          state.implementationContext.implementationSpecialistClient.filteredByIsActive,
  );
  let shouldShowErrorMessage = false
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const validateForm = async (values: {clientId: number, employerName: string}) => {
    const errors = {};
    let message = `Please resolve the following issues: `;
    try {
      await Yup.object({
        clientId: Yup.number().transform((value, originalValue) => {
          return originalValue < 1 ? null : value
        }).nullable().required('Client is required'),
        employerName: Yup.string().required("Employer Name is required"),
      }).validate(values, {abortEarly: false});
    } catch (err: any) {

      err.inner.forEach((error: any) => {
        // @ts-ignore
        errors[error.path] = error.message;
        message += ` ${error.message};`;
      });

      if (shouldShowErrorMessage) {
        dispatch(showErrorStatus(message));
        shouldShowErrorMessage = false
      }
    }
    return errors;
  };

  const formik = useFormik({
    initialValues: {
      clientId: selectedClientId,
      employerName: ""
    },
    onSubmit: () => {
    },
    validate: validateForm,
    enableReinitialize: true
  });

  const saveButtonOnClick = async () => {

    setIsLoading(true);
    shouldShowErrorMessage = true

    const errors = await formik.validateForm();

    if (Object.keys(errors).length === 0) {

      await dispatch(createEmployer({
        clientId: formik.values.clientId,
        employerName: formik.values.employerName
      }))
      formik.resetForm()
      modalOpenState.setIsModalOpen(false)
    }
    setIsLoading(false);
  };

  return (
      <Dialog
          scroll="body"
          className="modal client-modal"
          open={modalOpenState.isModalOpen}
          fullWidth={true}
          maxWidth="sm"
          test-id="client-employer-modal"
      >
        <DialogTitle>
        <span className="title">
          Add Client Employer
        </span>
        </DialogTitle>
        <DialogContent>
          <form>
            <Grid container spacing={2}>
              <Grid xs={12} item className="MuiTextField-root">
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel id="client-select-label">Client</InputLabel>
                  <Select
                      labelId="client-select-label"
                      label="Client"
                      name="clientId"
                      value={formik.values.clientId}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      test-id="client-employer-modal-client"
                      variant="outlined"
                      required
                      MenuProps={{
                        disablePortal: true,
                        disableAutoFocusItem: true,
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                        PaperProps: {
                          style: {
                            maxHeight: 500,
                            marginTop: 4,
                          },
                        },
                        MenuListProps: {
                          style: {
                            paddingTop: 0,
                            paddingBottom: 0,
                          },
                        },
                      }}
                  >
                    <MenuItem value="-1">Select</MenuItem>
                    {clients.map((client: ClientStatusCardViewModel) => (
                        <MenuItem key={client.clientId} value={client.clientId}>
                          {client.clientName}
                        </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                    name="employerName"
                    label="Employer Name"
                    value={formik.values.employerName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter Employer Name"
                    test-id="client-employer-modal-employer-name"
                    required
                />
              </Grid>
            </Grid>
          </form>

        </DialogContent>
        <DialogActions>
          <DialogActionButton
              isEnabled={!isLoading}
              savebuttonText="Save"
              saveStatus={0}
              executeSave={async () => {
                await saveButtonOnClick()
              }}
              handleCallbackSave={() => {
              }}
              handleCallbackCancel={()=>{
                formik.resetForm()
                modalOpenState.setIsModalOpen(false)
              }}
          />
        </DialogActions>
      </Dialog>
  );
};